import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import "./GeneralInvoiceFormStyles.css";
import { IPaymentAccount } from "src/stores/krediflow/InvoiceTypes";
import { INVOICE_PAYMENT_INFO_PROPERTY, INVOICE_PROPERTY, PAYMENT_INFO_TYPE } from "src/stores/krediflow/InvoiceEnums";
import styles from "./InvoiceDetailFormFields.module.css";
import { AiFieldFilledHint } from "src/screens/creditors/creditor/details/form/fields/AiFieldFilledHint";
import {
    formatAmountNumberToCurrencyString,
    formatDate,
    formatNumberWithApostrophies,
    formatReferenceNumberQRR,
    formatReferenceNumberSCOR
} from "src/stores/krediflow/Utils";
import { friendlyFormatIBAN } from "ibantools";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { completeShorthandYear, completeShorthandYearWhileTyping } from "src/utils/Date";
import { formatCurrency } from "src/utils/Currency";
import { format } from "date-fns-tz";
import { addDays } from "date-fns";

const InvoiceDetailFormFieldsBase = () => {
    const { t } = useTranslation();

    const amountRef = useRef<HTMLInputElement>(null);
    const paymentAccountInput = useRef<HTMLInputElement>(null);
    const paymentAccountFieldRef = useRef<HTMLDivElement>(null);
    const formFieldsRef = useRef<HTMLDivElement>(null);

    const { uiStore, creditorSearchStore, invoiceStore, paymentAccountSearchStore, navStore } =
        useContext(RootStoreContext);

    const [displayPaymentAccountList, setDisplayPaymentAccountList] = useState<boolean>(false);
    const [lastScrollPositionYPaymentAccountItems, setLastSrollYPositionPaymentAccountItems] = useState<number>(0);
    const [displayedAmount, setDisplayedAmount] = useState<string>(
        invoiceStore.currentInvoice && invoiceStore.currentInvoice.amount
            ? formatAmountNumberToCurrencyString(invoiceStore.currentInvoice.amount)
            : ""
    );
    const [updatingDisplayedAmount, setUpdatingDisplayedAmount] = useState<boolean>(false);

    const screenSize =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? uiStore.currentScreenSize : uiStore.invoicePaneScreenSize;

    useEffect(() => {
        if (invoiceStore.scrollToInvoiceDetails) {
            if (formFieldsRef.current) {
                formFieldsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            invoiceStore.setScrollToInvoiceDetails(false);
        }
        // eslint-disable-next-line
    }, [invoiceStore.scrollToInvoiceDetails]);

    const resetSearchQuery = useCallback(() => {
        paymentAccountSearchStore.setCurrentSelectedIBAN(paymentAccountSearchStore.currentSelectedIBAN ?? "");
    }, [paymentAccountSearchStore]);

    useEffect(() => {
        // Containment instead of a class whitelist: the list closes on this mousedown, before the
        // row's click fires, so any unlisted element inside a row swallowed the selection.
        const handleClickOutsidePaymentAccountInput = (event: MouseEvent) => {
            if (
                displayPaymentAccountList &&
                paymentAccountFieldRef.current &&
                !paymentAccountFieldRef.current.contains(event.target as Node)
            ) {
                setDisplayPaymentAccountList(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutsidePaymentAccountInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsidePaymentAccountInput);
        };
    }, [
        displayPaymentAccountList,
        resetSearchQuery,
        creditorSearchStore.currentCreditorSearchQuery,
        creditorSearchStore.selectedCreditorQueryString,
        invoiceStore.currentInvoice?.isCreditorSelected
    ]);

    useEffect(() => {
        if (paymentAccountSearchStore.triggerFocusOnPaymentAccountInput) {
            paymentAccountInput.current?.focus();
            paymentAccountSearchStore.setTriggerFocusOnPaymentAccountInput(false);
        }
        // This useEffect only listens to changes in contactPersonSearchStore.triggerFocusOnPersonInput
        // eslint-disable-next-line
    }, [paymentAccountSearchStore.triggerFocusOnPaymentAccountInput]);

    useEffect(() => {
        if (invoiceStore.isEditing) {
            const amountElement = amountRef.current;

            if (amountElement) {
                const handleWheel = (e: WheelEvent) => {
                    e.preventDefault(); // Prevent default scroll behavior of adding or subtracting 1 from the amount field
                };

                amountElement.addEventListener("wheel", handleWheel, { passive: false });

                // Cleanup listener on unmount
                return () => {
                    amountElement.removeEventListener("wheel", handleWheel);
                };
            }
        }
    }, [invoiceStore.isEditing]);

    const onSelectPaymentAccountItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedPaymentAccountItem =
                paymentAccountSearchStore.filteredPaymentAccountData[activeListItemIndex];

            paymentAccountSearchStore.updateInvoiceStoreWithSelectedPaymentAccount(selectedPaymentAccountItem);

            paymentAccountSearchStore.setCurrentSelectedIBAN(selectedPaymentAccountItem.iban ?? "");
        },
        [paymentAccountSearchStore]
    );

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "paymentaccount",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayPaymentAccountList,
        isListDisplayed: displayPaymentAccountList,
        onSelectItem: onSelectPaymentAccountItem,
        resetSearchQuery: resetSearchQuery,
        focusNextOnEnter: true
    });

    const resetAmountOfItemsDisplayedInPaymentAccountList = useCallback(() => {
        setLastSrollYPositionPaymentAccountItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeIBANInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInPaymentAccountList();

            const inputIBAN = e.target.value;

            paymentAccountSearchStore.setCurrentSelectedIBAN(inputIBAN);
            paymentAccountSearchStore.updateInvoiceStoreWithManuallyEnteredIBAN(inputIBAN);
        },

        [paymentAccountSearchStore, resetAmountOfItemsDisplayedInPaymentAccountList]
    );

    const onBlurIBANInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInPaymentAccountList();

            let inputIBAN = e.target.value;

            // Remove all whitespaces that might have been entered
            inputIBAN = inputIBAN.replace(/\s+/g, "");

            if (inputIBAN) {
                paymentAccountSearchStore.updateInvoiceStoreWithManuallyEnteredIBAN(inputIBAN);
            }
        },
        [resetAmountOfItemsDisplayedInPaymentAccountList, paymentAccountSearchStore]
    );

    const updateAmountString = useCallback(
        (amount: string, newInvoiceNumberValue: number | null) => {
            if (!updatingDisplayedAmount && amount !== displayedAmount) {
                setUpdatingDisplayedAmount(true);
                setDisplayedAmount(amount);

                if (newInvoiceNumberValue !== null && invoiceStore.currentInvoice) {
                    invoiceStore.currentInvoice.updateProperty(INVOICE_PROPERTY.AMOUNT, newInvoiceNumberValue);
                }

                setUpdatingDisplayedAmount(false);
            }
        },
        [displayedAmount, invoiceStore.currentInvoice, updatingDisplayedAmount]
    );

    useEffect(() => {
        if (invoiceStore.currentInvoice && invoiceStore.currentInvoice.amount) {
            const currentAmountAsFloat = parseFloat(displayedAmount.replace(/'/g, ""));
            const amountInStoreAsFloat = parseFloat(invoiceStore.currentInvoice.amount.toString());
            if (currentAmountAsFloat !== amountInStoreAsFloat) {
                const amountFromMapExtractedDataOnCurrentInvoice =
                    invoiceStore.currentInvoice && invoiceStore.currentInvoice.amount
                        ? formatAmountNumberToCurrencyString(invoiceStore.currentInvoice?.amount)
                        : "";
                updateAmountString(amountFromMapExtractedDataOnCurrentInvoice, null);
            }
        }

        if (
            invoiceStore.currentInvoice?.isNewInvoice &&
            invoiceStore.currentInvoice?.amount === undefined &&
            displayedAmount.length > 0
        ) {
            // clear amount on init new invoice
            setDisplayedAmount("");
        }
    }, [updateAmountString, invoiceStore.currentInvoice, invoiceStore.currentInvoice?.amount, displayedAmount]);

    const onFocusPaymentAccountInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (paymentAccountInput.current) {
                paymentAccountInput.current.select();
            }

            resetAmountOfItemsDisplayedInPaymentAccountList();
            setDisplayPaymentAccountList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [resetAmountOfItemsDisplayedInPaymentAccountList, setActiveListItemIndex, NO_LIST_ITEM_SELECTED]
    );

    const onClickPaymentAccountItem = useCallback(
        (paymentAccount: IPaymentAccount) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayPaymentAccountList(false);

            paymentAccountSearchStore.updateInvoiceStoreWithSelectedPaymentAccount(paymentAccount);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, paymentAccountSearchStore]
    );

    const onScrollPaymentAccountList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYPaymentAccountItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems < paymentAccountSearchStore.filteredPaymentAccountData.length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionPaymentAccountItems(pixelsFromTop);
        },
        [
            lastScrollPositionYPaymentAccountItems,
            paymentAccountSearchStore.filteredPaymentAccountData.length,
            invoiceStore
        ]
    );

    const getSpanElementWithFormattedText = useCallback(
        (stringToFormat: string) => {
            const formattedString =
                paymentAccountSearchStore.currentSelectedIBAN !== ""
                    ? boldenMatchedSubstring(
                          paymentAccountSearchStore.currentSelectedIBAN ?? "",
                          stringToFormat,
                          "highlighted-text paymentaccount"
                      )
                    : stringToFormat;

            return (
                <span
                    className="paymentaccount-list-item-span"
                    dangerouslySetInnerHTML={{
                        __html: formattedString
                    }}
                />
            );
        },
        [paymentAccountSearchStore.currentSelectedIBAN]
    );

    const onChangeInvoiceFormField = useCallback(
        (invoiceProperty: INVOICE_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue: any = e.target.value;

            if (invoiceProperty === INVOICE_PROPERTY.AMOUNT) {
                const newDisplayedValue = newValue.replace(/'/g, "");
                newValue = parseFloat(newDisplayedValue);
                updateAmountString(formatNumberWithApostrophies(newDisplayedValue), newValue);

                return;
            }

            if (invoiceProperty === INVOICE_PROPERTY.DATE || invoiceProperty === INVOICE_PROPERTY.DUE_DATE) {
                newValue = completeShorthandYearWhileTyping(newValue);
            }

            if (invoiceProperty === INVOICE_PROPERTY.DATE && newValue) {
                // set the dueDate to 30 days in the future of the date (newValue)
                const dueDate = format(addDays(new Date(newValue), 30), "yyyy-MM-dd");

                invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.DUE_DATE, dueDate);
            }

            if (invoiceStore.currentInvoice) {
                invoiceStore.currentInvoice.updateProperty(invoiceProperty, newValue);

                if (invoiceProperty === INVOICE_PROPERTY.DATE) {
                    invoiceStore.currentInvoice.followInvoiceDateWithBookingDate(newValue);
                }
            }
        },
        [invoiceStore.currentInvoice, updateAmountString]
    );

    const onBlurInvoiceAmount = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            let newValue = e.target.value;

            newValue = newValue.replace(/'/g, "");
            const newValueNumber = parseFloat(newValue);

            updateAmountString(formatAmountNumberToCurrencyString(newValueNumber), null);
        },
        [updateAmountString]
    );

    const completeInvoiceDateYear = useCallback(
        (invoiceProperty: INVOICE_PROPERTY, enteredDate: string) => {
            const currentInvoice = invoiceStore.currentInvoice;
            const completedDate = completeShorthandYear(enteredDate);

            if (!currentInvoice || completedDate === enteredDate) {
                return;
            }

            currentInvoice.updateProperty(invoiceProperty, completedDate);

            if (invoiceProperty === INVOICE_PROPERTY.DATE) {
                currentInvoice.followInvoiceDateWithBookingDate(completedDate);
            }

            if (invoiceProperty === INVOICE_PROPERTY.DATE && currentInvoice.dueDate) {
                // the dueDate was set from the shorthand date and needs the same completed year
                currentInvoice.updateProperty(INVOICE_PROPERTY.DUE_DATE, completeShorthandYear(currentInvoice.dueDate));
            }
        },
        [invoiceStore.currentInvoice]
    );

    const onBlurInvoiceDate = useCallback(
        (invoiceProperty: INVOICE_PROPERTY) => (e: React.FocusEvent<HTMLInputElement>) => {
            completeInvoiceDateYear(invoiceProperty, e.target.value);

            invoiceStore.currentInvoice?.validate();
        },
        [completeInvoiceDateYear, invoiceStore.currentInvoice]
    );

    // the shorthand year has to be completed before Enter triggers an action on the invoice
    const onKeyDownInvoiceDate = useCallback(
        (invoiceProperty: INVOICE_PROPERTY) => (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                completeInvoiceDateYear(invoiceProperty, e.currentTarget.value);
            }
        },
        [completeInvoiceDateYear]
    );

    const onChangePaymentInfoFormField = useCallback(
        (paymentInfoProperty: INVOICE_PAYMENT_INFO_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue: any = e.target.value;

            if (invoiceStore.currentInvoice) {
                invoiceStore.currentInvoice.updatePaymentInfoProperty(paymentInfoProperty, newValue);
            }
        },
        [invoiceStore.currentInvoice]
    );

    const onBlurInvoiceReferenceInput = useCallback(() => {
        if (invoiceStore.currentInvoice?.paymentInfo?.reference) {
            invoiceStore.currentInvoice?.validateInvoiceReferenceNumber(
                invoiceStore.currentInvoice?.paymentInfo?.reference
            );
        }
    }, [invoiceStore.currentInvoice]);

    const onBlurInvoiceNumberField = useCallback(() => {
        if (invoiceStore.currentInvoice && invoiceStore.currentInvoice?.invoiceNumber) {
            invoiceStore.currentInvoice.getDuplicatesAndDisplayModal();
        }
    }, [invoiceStore.currentInvoice]);

    const paymentAccountSelectionList = (
        <div id="paymentaccount-dropdown-list-container">
            <div
                className={`${styles.PaymentAccountSelectionListSearchIndicator} ${
                    !paymentAccountSearchStore.isSearchingPaymentAccountData &&
                    !(
                        paymentAccountSearchStore.paymentAccountData.length === 0 &&
                        !paymentAccountSearchStore.isSearchingPaymentAccountData
                    )
                        ? styles.NoBorderBottom
                        : ""
                }`}
            >
                {paymentAccountSearchStore.isSearchingPaymentAccountData ? (
                    <UI.RotatingSpinner noLogo size={30} className={styles.SearchingPaymentAccountSpinner} />
                ) : undefined}
            </div>
            {paymentAccountSearchStore.isSearchingPaymentAccountData ? undefined : (
                <ul
                    id="paymentaccount-dropdown-list"
                    className={"dropdown-list paymentaccount"}
                    onScroll={onScrollPaymentAccountList}
                >
                    {paymentAccountSearchStore.filteredPaymentAccountData
                        .slice(0, invoiceStore.numberOfDisplayedItems)
                        .map((paymentAccount, index) => {
                            const formattedPaymentAccountNameSpan = getSpanElementWithFormattedText(
                                paymentAccount.name ?? ""
                            );
                            const formattedPaymentAccountIBANSpan = getSpanElementWithFormattedText(
                                paymentAccount.iban ?? ""
                            );

                            return (
                                <li key={index}>
                                    <div
                                        className={"list-item paymentaccount"}
                                        id={`paymentaccount-list-item-${index}`}
                                        onClick={onClickPaymentAccountItem(paymentAccount)}
                                    >
                                        {uiStore.isMobile ? (
                                            <>
                                                <div>{formattedPaymentAccountNameSpan}</div>
                                                <div>{formattedPaymentAccountIBANSpan}</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className={"paymentaccount-list-item-line-1"}>
                                                    {formattedPaymentAccountNameSpan}
                                                </div>
                                                <div className={"paymentaccount-list-item-line-2"}>
                                                    {formattedPaymentAccountIBANSpan}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                </ul>
            )}
        </div>
    );

    const currentInvoice = invoiceStore.currentInvoice;

    const editModePaymentAccountElements = (
        <div ref={formFieldsRef} id="edit-mode-invoice-form-fields">
            <UI.Row>
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-3" : "col-6"}>
                    <div className={styles.AiFieldWrapper}>
                        <UI.Input
                            label={`${t("screens.kredi_flow.form.invoice.date").toString()}*`}
                            type="date"
                            min={"2000-01-01"}
                            max={"2100-12-31"}
                            autoComplete="off"
                            value={
                                invoiceStore.currentInvoice && invoiceStore.currentInvoice.date
                                    ? invoiceStore.currentInvoice.date
                                    : ""
                            }
                            onChange={onChangeInvoiceFormField(INVOICE_PROPERTY.DATE)}
                            onBlur={onBlurInvoiceDate(INVOICE_PROPERTY.DATE)}
                            onKeyDown={onKeyDownInvoiceDate(INVOICE_PROPERTY.DATE)}
                        />
                        {invoiceStore.currentInvoice?.errors.dateIsRequired && (
                            <span className={styles.DateError}>
                                {invoiceStore.currentInvoice?.errors.dateIsRequired}
                            </span>
                        )}
                        {invoiceStore.currentInvoice?.errors.dateIsOutOfRange && (
                            <span className={styles.DateError}>
                                {invoiceStore.currentInvoice?.errors.dateIsOutOfRange}
                            </span>
                        )}
                        <AiFieldFilledHint field={INVOICE_PROPERTY.DATE} />
                    </div>
                    <div className={styles.AiFieldWrapper}>
                        <UI.Input
                            label={t("screens.kredi_flow.form.invoice.due_date").toString()}
                            type="date"
                            min={"2000-01-01"}
                            max={"2100-12-31"}
                            autoComplete="off"
                            value={
                                invoiceStore.currentInvoice && invoiceStore.currentInvoice.dueDate
                                    ? invoiceStore.currentInvoice.dueDate
                                    : ""
                            }
                            onChange={onChangeInvoiceFormField(INVOICE_PROPERTY.DUE_DATE)}
                            onBlur={onBlurInvoiceDate(INVOICE_PROPERTY.DUE_DATE)}
                            onKeyDown={onKeyDownInvoiceDate(INVOICE_PROPERTY.DUE_DATE)}
                            className={
                                invoiceStore.currentInvoice?.errors.dueDateIsBeforeInvoiceDate ? "error-text" : ""
                            }
                        />
                        {invoiceStore.currentInvoice?.errors.dueDateIsBeforeInvoiceDate && (
                            <span className={styles.DueDateError}>
                                {invoiceStore.currentInvoice?.errors.dueDateIsBeforeInvoiceDate}
                            </span>
                        )}
                        {invoiceStore.currentInvoice?.errors.dueDateIsOutOfRange && (
                            <span className={styles.DateError}>
                                {invoiceStore.currentInvoice?.errors.dueDateIsOutOfRange}
                            </span>
                        )}
                        <AiFieldFilledHint field={INVOICE_PROPERTY.DUE_DATE} />
                    </div>
                </div>
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-4" : "col-6"}>
                    <div className={styles.AiFieldWrapper}>
                        <UI.Input
                            ref={amountRef}
                            label={t("screens.kredi_flow.form.invoice.amount").toString()}
                            type="text"
                            autoComplete="off"
                            value={displayedAmount}
                            onBlur={onBlurInvoiceAmount}
                            onChange={onChangeInvoiceFormField(INVOICE_PROPERTY.AMOUNT)}
                        />
                        <AiFieldFilledHint field={INVOICE_PROPERTY.AMOUNT} />
                    </div>
                    <div className={styles.AiFieldWrapper}>
                        <UI.Input
                            label={t("screens.kredi_flow.form.invoice.invoice_number").toString()}
                            type="text"
                            autoComplete="off"
                            value={
                                invoiceStore.currentInvoice && invoiceStore.currentInvoice.invoiceNumber
                                    ? invoiceStore.currentInvoice.invoiceNumber
                                    : ""
                            }
                            onBlur={onBlurInvoiceNumberField}
                            onChange={onChangeInvoiceFormField(INVOICE_PROPERTY.INVOICE_NUMBER)}
                        />
                        <AiFieldFilledHint field={INVOICE_PROPERTY.INVOICE_NUMBER} />
                    </div>
                </div>
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-5" : "col-12"}>
                    <div className="mb-3" ref={paymentAccountFieldRef}>
                        <UI.Input
                            label={t("screens.kredi_flow.form.invoice.iban").toString()}
                            ref={paymentAccountInput}
                            type="text"
                            autoComplete="off"
                            className={`${styles.PaymentAccountInputField} ${
                                invoiceStore.currentInvoice?.errors.ibanFormatInvalid ? "error-text" : ""
                            }`}
                            id="invoice-form-paymentaccount-input"
                            value={paymentAccountSearchStore.currentSelectedIBAN}
                            onChange={onChangeIBANInput}
                            onBlur={onBlurIBANInput}
                            onFocus={onFocusPaymentAccountInput}
                            onKeyDown={onKeyDownFunction}
                            disabled={!invoiceStore.isEditing}
                            iconRight={
                                <UI.Icon
                                    icon={UI.SVGIcon.Search}
                                    color="grey"
                                    size={"small"}
                                    style={{ right: "5px" }}
                                />
                            }
                            errorMsg={invoiceStore.currentInvoice?.errors.ibanFormatInvalid ?? ""}
                        />
                        {displayPaymentAccountList ? paymentAccountSelectionList : undefined}
                    </div>
                    <div className="mb-3">
                        <UI.Input
                            label={t("screens.kredi_flow.form.invoice.reference_number").toString()}
                            type="text"
                            autoComplete="off"
                            value={
                                invoiceStore.currentInvoice &&
                                invoiceStore.currentInvoice.paymentInfo &&
                                invoiceStore.currentInvoice.paymentInfo.reference
                                    ? invoiceStore.currentInvoice.paymentInfo.reference
                                    : ""
                            }
                            onChange={onChangePaymentInfoFormField(INVOICE_PAYMENT_INFO_PROPERTY.REFERENCE)}
                            onBlur={onBlurInvoiceReferenceInput}
                            errorMsg={invoiceStore.currentInvoice?.errors.referenceNumberFormatInvalid ?? ""}
                            className={
                                invoiceStore.currentInvoice?.errors.referenceNumberFormatInvalid ? "error-text" : ""
                            }
                        />
                    </div>
                    {navStore.hasOptionPaymentPurpose && (
                        <div>
                            <UI.Input
                                label={t("screens.kredi_flow.form.invoice.payment_purpose").toString()}
                                type="text"
                                autoComplete="off"
                                value={
                                    invoiceStore.currentInvoice && invoiceStore.currentInvoice.paymentPurpose
                                        ? invoiceStore.currentInvoice.paymentPurpose
                                        : ""
                                }
                                onChange={onChangeInvoiceFormField(INVOICE_PROPERTY.PAYMENT_PURPOSE)}
                            />
                        </div>
                    )}
                </div>
            </UI.Row>
        </div>
    );

    const viewCreditorElements = (
        <UI.Container fluid className="mt-3">
            <UI.Row>
                {/* First Column */}
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-3" : "col-12"}>
                    <UI.Row className={screenSize >= SCREEN_SIZE.LAPTOP ? "mb-4" : ""}>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">{t("screens.kredi_flow.form.invoice.date").toString()}</div>
                            <div>{currentInvoice?.date ? formatDate(currentInvoice?.date) : ""}</div>
                        </div>
                    </UI.Row>
                    <UI.Row>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">
                                {t("screens.kredi_flow.form.invoice.due_date").toString()}
                            </div>
                            <div>{currentInvoice?.dueDate ? formatDate(currentInvoice?.dueDate) : ""}</div>
                        </div>
                    </UI.Row>
                </div>

                {/* Second Column */}
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-4" : "col-12"}>
                    <UI.Row className={`${screenSize >= SCREEN_SIZE.LAPTOP ? "mb-4" : ""} `}>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">
                                {t("screens.kredi_flow.form.invoice.amount").toString()}
                            </div>
                            <div>{currentInvoice?.amount ? `CHF ${formatCurrency(currentInvoice.amount)}` : ""}</div>
                        </div>
                    </UI.Row>
                    <UI.Row>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">
                                {t("screens.kredi_flow.form.invoice.invoice_number").toString()}
                            </div>
                            <div>{currentInvoice?.invoiceNumber ? currentInvoice?.invoiceNumber : ""}</div>
                        </div>
                    </UI.Row>
                </div>

                {/* Third Column */}
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-5" : "col-12"}>
                    <UI.Row className={screenSize >= SCREEN_SIZE.LAPTOP ? "mb-4" : ""}>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">{t("screens.kredi_flow.form.invoice.iban").toString()}</div>
                            <div>
                                {currentInvoice && currentInvoice.paymentAccount && currentInvoice.paymentAccount.iban
                                    ? friendlyFormatIBAN(currentInvoice.paymentAccount.iban)
                                    : currentInvoice && currentInvoice.paymentInfo && currentInvoice.paymentInfo.iban
                                      ? friendlyFormatIBAN(currentInvoice.paymentInfo.iban)
                                      : ""}
                            </div>
                        </div>
                    </UI.Row>
                    <UI.Row className={screenSize >= SCREEN_SIZE.LAPTOP ? "mb-4" : ""}>
                        <div className={styles.InvoiceDetailInformationContainer}>
                            <div className="bold-text mb-1">
                                {t("screens.kredi_flow.form.invoice.reference_number").toString()}
                            </div>
                            <div>
                                {currentInvoice?.paymentInfo?.reference &&
                                currentInvoice.paymentInfo.type &&
                                currentInvoice.paymentInfo.type === PAYMENT_INFO_TYPE.QRR
                                    ? formatReferenceNumberQRR(currentInvoice?.paymentInfo?.reference)
                                    : currentInvoice?.paymentInfo?.reference &&
                                        currentInvoice.paymentInfo.type &&
                                        currentInvoice.paymentInfo.type === PAYMENT_INFO_TYPE.SCOR
                                      ? formatReferenceNumberSCOR(currentInvoice?.paymentInfo?.reference)
                                      : ""}
                            </div>
                        </div>
                    </UI.Row>
                    {navStore.hasOptionPaymentPurpose && (
                        <UI.Row>
                            <div className={styles.InvoiceDetailInformationContainer}>
                                <div className="bold-text mb-1">
                                    {t("screens.kredi_flow.form.invoice.payment_purpose").toString()}
                                </div>
                                <div>{currentInvoice?.paymentPurpose ? currentInvoice.paymentPurpose : ""}</div>
                            </div>
                        </UI.Row>
                    )}
                </div>
            </UI.Row>
        </UI.Container>
    );

    return invoiceStore.isEditing ? editModePaymentAccountElements : viewCreditorElements;
};

export const InvoiceDetailFormFields = Sentry.withProfiler(observer(InvoiceDetailFormFieldsBase));
